import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { selectCurrentUser } from '../auth/selectors';

/**
 * When a session ARRIVES, the user's stored locale wins — once.
 *
 * That is the client half of the amendment #20 precedence: `?lang=` (which the
 * base query sets from the UI language), then the **stored user locale**, then
 * `Accept-Language`, then French. Before login the UI follows the browser and
 * the pre-auth toggle; the moment the server tells us what this person chose,
 * that is what the screen speaks.
 *
 * **Once per identity, not a standing equality rule.** This used to re-assert
 * `user.locale` on every run, which was safe only while its stated premise
 * held — that a signed-in language change always posts `/me/locale`, so the
 * two values could never stay apart. `/pin` breaks that premise by design:
 * D-05 refuses `/me/locale` to the one-time-PIN token, so the toggle there
 * can only move i18next and the stored locale stays stale on purpose. Under
 * the old rule every later run of this effect — a re-render with a fresh
 * `auth.user` object from a session refetch, a `bootstrapSession` landing,
 * a StrictMode remount — quietly undid the choice, and a toggle that flips
 * back the instant you press it is indistinguishable from a dead button.
 *
 * Keying on `user.id` keeps every case it exists for (first login, a reload,
 * signing in as somebody else) and drops only the re-assertion that fought
 * the user. `changePin` pushes the deferred choice once the restriction lifts.
 */
export function LocaleSync() {
  const { i18n } = useTranslation();
  const user = useSelector(selectCurrentUser);
  const appliedFor = useRef<string | null>(null);

  useEffect(() => {
    if (user === null) {
      // Signing out has to re-arm it, or the next session on this tab would
      // keep whatever language the previous one ended on.
      appliedFor.current = null;
      return;
    }
    if (appliedFor.current === user.id) return;
    appliedFor.current = user.id;
    if (user.locale !== i18n.language) void i18n.changeLanguage(user.locale);
  }, [user, i18n]);

  return null;
}
